import React, { useEffect, useState, useCallback, useMemo } from "react";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import axios from "axios";
import HOST_URL from "../../../utils/Url";
import Card from "@mui/material/Card";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Paper from "@mui/material/Paper";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp";
import DeleteIcon from "@mui/icons-material/Delete";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import CircularProgress from "@mui/material/CircularProgress";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDetailSwipeableDrawer from "./FileDetailSwipeableDrawer";
import { getSecureDevId, getSecureSubDeptId } from "../../../utils/storageHelpers";
import PublishIcon from "@mui/icons-material/Publish";
import { secureGetItem } from "../../../utils/encryption";
import { usePermissionData } from "../../../hooks/usePermissionData";

interface MenuPermission {
  isAdd: boolean;
  isEdit: boolean;
  isPrint: boolean;
  isDel: boolean;
}

interface DocumentData {
  docMid: number;
  fileNo: string;
  synopsis: string;
  fileDef: string;
  id: number;
  serialNo: number;
}

export default function DocumentEntry() {
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [selectedExcelFile, setSelectedExcelFile] = useState<File | null>(null);
  const [docDetailData, setDocDetailData] = useState<string | null>(null);
  const [docDetailRecords, setDocDetailRecords] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  const devId = getSecureDevId();
  const subDeptID = getSecureSubDeptId();

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { permissionData, loading: permissionLoading } = usePermissionData();

  // Memoized fetch function
  const fetchZonesData = useCallback(async () => {
    try {
      setIsLoading(true);
      const collectData = {
        docMid: -1,
        fileTypId: -1,
        //@ts-ignore
        divisionid: devId === "null" ? -1 : devId,
        subsubjId: -1,
        user_Id: -1,
        fileNo: "",
        subDeptID: subDeptID == null ? -1 : subDeptID,
        synopsis: "",
        complt: ""
      };

      const response = await axios.post(
        HOST_URL.HOST_URL2 + `DocMangr/GetDocMangr`,
        collectData
      );

      if (response.data.data) {
        const documentsWithIds = response.data.data.map((doc: any, index: number) => ({
          ...doc,
          serialNo: index + 1,
          id: doc.docMid,
        }));
        setDocuments(documentsWithIds);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load documents");
    } finally {
      setIsLoading(false);
    }
  }, [devId, subDeptID]);

  // Initial data fetch
  useEffect(() => {
    fetchZonesData();
  }, [fetchZonesData]);

  // Setup columns based on permission data
  useEffect(() => {
    if (permissionLoading || documents.length === 0) return;

    const baseColumns: GridColDef[] = [
      {
        field: "serialNo",
        headerName: t("text.SrNo"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 60
      },
      {
        field: "fileNo",
        headerName: t("text.FileNo"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 140
      },
      {
        field: "synopsis",
        headerName: t("text.FileName"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 140
      },
      {
        field: "fileDef",
        headerName: t("text.FileDef"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 140
      },
    ];

    // Add actions column only if user has edit, delete, or view permissions
    if (permissionData?.isEdit || permissionData?.isDel) {
      const actionsColumn: GridColDef = {
        field: "actions",
        headerClassName: "MuiDataGrid-colCell",
        headerName: t("text.Action"),
        width: 150,
        renderCell: (params) => (
          <Stack
            spacing={1}
            direction="row"
            sx={{ alignItems: "center", marginTop: "5px" }}
          >
            {permissionData?.isEdit && (
              <EditIcon
                style={{
                  fontSize: "20px",
                  color: "blue",
                  cursor: "pointer",
                }}
                className="cursor-pointer"
                onClick={() => routeChangeEdit(params.row)}
              />
            )}
            {permissionData?.isDel && (
              <DeleteIcon
                style={{
                  fontSize: "20px",
                  color: "red",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setDeleteId(params.row.id.toString());
                  confirmDialog({
                    message: "Do you want to delete this record?",
                    header: "Delete Confirmation",
                    icon: "pi pi-info-circle",
                    acceptClassName: "p-button-danger",
                    accept: () => handleDeleteAccept(params.row.id),
                    reject: handleDeleteReject,
                  });
                }}
              />
            )}
            {/* Always show view icon if user can see the grid */}
            <VisibilityIcon
              style={{
                fontSize: "20px",
                color: "blue",
                cursor: "pointer",
              }}
              onClick={() => {
                getDocMngRecords(params.row.id);
              }}
            />
          </Stack>
        ),
      };

      setColumns([actionsColumn, ...baseColumns]);
    } else {
      // If no edit/delete permissions, just show view icon
      const viewOnlyColumn: GridColDef = {
        field: "actions",
        headerClassName: "MuiDataGrid-colCell",
        headerName: t("text.Action"),
        width: 100,
        renderCell: (params) => (
          <Stack
            spacing={1}
            direction="row"
            sx={{ alignItems: "center", marginTop: "5px" }}
          >
            <VisibilityIcon
              style={{
                fontSize: "20px",
                color: "blue",
                cursor: "pointer",
              }}
              onClick={() => {
                getDocMngRecords(params.row.id);
              }}
            />
          </Stack>
        ),
      };
      setColumns([viewOnlyColumn, ...baseColumns]);
    }
  }, [permissionData, permissionLoading, documents.length, t]);

  // Navigation handlers
  const routeChangeAdd = () => {
    navigate(`/DocManagement/DocumentEntryAdd`);
  };

  const routeChangeEdit = (row: DocumentData) => {
    navigate(`/DocManagement/DocumentEntryEdit`, {
      state: row,
    });
  };

  // Delete handlers
  const handleDeleteAccept = useCallback(async (id: number) => {
    try {
      const data = secureGetItem("userdata");
      const userId = data && data[0] ? data[0].useR_ID : "0";

      const collectData = {
        docMid: id,
        userID: userId,
      };

      const response = await axios.post(
        HOST_URL.HOST_URL2 + `DocMangr/DeleteDocMangr`,
        collectData
      );

      if (response.data.isSuccess) {
        toast.success(response.data.mesg);
        fetchZonesData();
      } else {
        toast.error(response.data.mesg);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete document");
    } finally {
      setDeleteId("");
    }
  }, [fetchZonesData]);

  const handleDeleteReject = useCallback(() => {
    toast.warn("Deletion cancelled", { autoClose: 3000 });
    setDeleteId("");
  }, []);

  // View document details
  const getDocMngRecords = useCallback(async (id: number) => {
    try {
      const collectData = {
        docMid: id,
        fileTypId: -1,
        divisionid: -1,
        subsubjId: -1,
        user_Id: -1,
        fileNo: "",
        subDeptID: -1,
        synopsis: "",
        complt: ""
      };

      const response = await axios.post(
        HOST_URL.HOST_URL2 + `DocMangr/GetDocMangr`,
        collectData
      );

      if (response.data.data && response.data.data.length > 0) {
        const firstRecord = response.data.data[0];
        setDocDetailData(`${firstRecord.fileNo} - ${firstRecord.fileType_Name}`);
        setDocDetailRecords(response.data.data);
        setDrawerOpen(true);
      }
    } catch (error) {
      console.error("Error fetching document details:", error);
      toast.error("Failed to load document details");
    }
  }, []);

  // Excel file selection
  const handleExcelFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    setSelectedExcelFile(file);
    setExcelModalOpen(true);
  };

  // Final Excel import
  const handleFinalExcelImport = useCallback(async () => {
    if (!selectedExcelFile) {
      toast.error("No file selected");
      return;
    }

    setImportLoading(true);

    try {
      const data = secureGetItem("userdata");
      const userId = data && data[0] ? data[0].useR_ID : "";

      const formData = new FormData();
      formData.append("file", selectedExcelFile);
      formData.append("FileName", selectedExcelFile.name);
      formData.append("userId", userId);

      const response = await axios.post(
        `${HOST_URL.HOST_URL}Import/ImportExcel`,
        formData
      );

      if (response.data.success) {
        toast.success("Excel imported successfully!");
        fetchZonesData();
      } else {
        toast.error(response.data.mesg || "Failed to import Excel");
      }
    } catch (error) {
      console.error("Excel import error:", error);
      toast.error("Error importing Excel");
    } finally {
      setImportLoading(false);
      setExcelModalOpen(false);
      setSelectedExcelFile(null);
    }
  }, [selectedExcelFile, fetchZonesData]);

  // Memoized adjusted columns
  const adjustedColumns = useMemo(() =>
    columns.map(column => ({ ...column })),
    [columns]
  );

  // Loading state handling
  if (permissionLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card
        style={{
          width: "100%",
          marginTop: "3vh",
          backgroundColor: "#E9FDEE",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            "& .MuiDataGrid-colCell": {
              backgroundColor: `var(--grid-headerBackground)`,
              color: `var(--grid-headerColor)`,
              fontSize: 17,
              fontWeight: 900
            },
          }}
          style={{ padding: "10px" }}
        >
          <ConfirmDialog />

          <Typography
            gutterBottom
            variant="h5"
            component="div"
            sx={{ padding: "20px" }}
            align="left"
          >
            {t("text.DocumentEntry")}
          </Typography>
          <Divider />

          <Box height={10} />

          {/* Action Buttons - Only show if user has add permission */}
          {(permissionData?.isAdd || permissionData?.isEdit) && (
            <Stack direction="row" spacing={2} classes="my-2 mb-2">
              {permissionData?.isAdd && (
                <Button
                  onClick={routeChangeAdd}
                  variant="contained"
                  endIcon={<AddCircleIcon />}
                  size="large"
                  style={{ backgroundColor: `var(--header-background)` }}
                >
                  {t("text.add")}
                </Button>
              )}

              {/* Excel Import Button - Only show if user has add permission */}
              {permissionData?.isAdd && (
                <Button
                  component="label"
                  variant="contained"
                  endIcon={<PublishIcon />}
                  size="large"
                  style={{ backgroundColor: `var(--header-background)` }}
                  disabled={importLoading}
                >
                  {importLoading ? "Importing..." : "Import Excel"}
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    onChange={handleExcelFileSelect}
                  />
                </Button>
              )}
            </Stack>
          )}

          {/* Document Details Drawer */}
          <FileDetailSwipeableDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            title={`# ${docDetailData}  `}
            userData={docDetailRecords}
          />

          {/* Data Grid Section */}
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <Box>
              <br />
              <div style={{ width: "100%", backgroundColor: `var(--grid-background)` }}>
                <DataGrid
                  rows={documents}
                  columns={adjustedColumns}
                  autoHeight
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  rowSpacingType="border"
                  pagination={true}
                  pageSizeOptions={[5, 10, 25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                    },
                  }}
                  disableRowSelectionOnClick
                />
              </div>
            </Box>
          )}
        </Paper>
      </Card>

      {/* Excel Import Confirmation Modal */}
      <Dialog open={excelModalOpen} onClose={() => setExcelModalOpen(false)}>
        <DialogTitle>Confirm Excel Import</DialogTitle>
        <DialogContent>
          <Typography>
            Selected File: <strong>{selectedExcelFile?.name}</strong>
          </Typography>
          <Typography mt={1}>
            Click <strong>Import</strong> to upload this Excel file.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={() => setExcelModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFinalExcelImport}
            disabled={importLoading}
            style={{ backgroundColor: `var(--header-background)` }}
          >
            {importLoading ? "Importing..." : "Import"}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastApp />
    </>
  );
}



















// import React, { useEffect, useState } from "react";
// import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
// import axios from "axios";
// import HOST_URL from "../../../utils/Url";
// import Card from "@mui/material/Card";
// import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
// import EditIcon from "@mui/icons-material/Edit";
// import Switch from "@mui/material/Switch";
// import { useNavigate, useLocation } from "react-router-dom";
// import Chip from "@mui/material/Chip";
// import { useTranslation } from "react-i18next";
// import Paper from "@mui/material/Paper";
// import AddCircleIcon from "@mui/icons-material/AddCircle";
// import { toast } from "react-toastify";
// import ToastApp from "../../../ToastApp";
// import DeleteIcon from "@mui/icons-material/Delete";
// import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
// import CircularProgress from "@mui/material/CircularProgress";
// import VisibilityIcon from "@mui/icons-material/Visibility";
// import FileDetailSwipeableDrawer from "./FileDetailSwipeableDrawer";
// import { getSecureDevId, getSecureSubDeptId } from "../../../utils/storageHelpers";
// import PublishIcon from "@mui/icons-material/Publish";
// import DownloadIcon from "@mui/icons-material/Download"; // Import download icon
// import { secureGetItem } from "../../../utils/encryption";

// interface MenuPermission {
//   isAdd: boolean;
//   isEdit: boolean;
//   isPrint: boolean;
//   isDel: boolean;
// }

// export default function DocumentEntry() {

//   const [zones, setZones] = useState([]);
//   const [columns, setColumns] = useState<any>([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const location = useLocation();
//   const [excelModalOpen, setExcelModalOpen] = useState(false);
//   const [selectedExcelFile, setSelectedExcelFile] = useState<File | null>(null);

//   const [permissionData, setPermissionData] = useState<MenuPermission>({
//     isAdd: false,
//     isEdit: false,
//     isPrint: false,
//     isDel: false,
//   });
//   const [DocDetailData, setDocDetailData] = useState<any>(null);
//   const [DocDetailRecords, setDocDetailRecords] = useState<any>(null);
//   const [drawerOpen, setDrawerOpen] = useState(false);
//   const [importLoading, setImportLoading] = useState(false);
//   const devId = getSecureDevId();
//   const subDeptID = getSecureSubDeptId();

//   let navigate = useNavigate();
//   const { t } = useTranslation();

//   useEffect(() => {
//     const dataString = localStorage.getItem("userdata");
//     fetchZonesData();
//   }, []);

//   const routeChangeAdd = () => {
//     let path = `/DocManagement/DocumentEntryAdd`;
//     navigate(path);
//   };

//   const routeChangeEdit = (row: any) => {
//     let path = `/DocManagement/DocumentEntryEdit`;
//     navigate(path, {
//       state: row,
//     });
//   };

//   let delete_id = "";

//   const accept = () => {
//     const collectData = {
//       docMid: delete_id,
//       userID: 0,
//     };
//     console.log("collectData " + JSON.stringify(collectData));
//     axios
//       .post(HOST_URL.HOST_URL2 + `DocMangr/DeleteDocMangr`,
//         collectData
//       )
//       .then((response) => {
//         if (response.data.isSuccess) {
//           toast.success(response.data.mesg);
//         } else {
//           toast.error(response.data.mesg);
//         }
//         fetchZonesData();
//       });
//   };

//   const reject = () => {
//     toast.warn("Rejected: You have rejected", { autoClose: 3000 });
//   };

//   const handledeleteClick = (del_id: any) => {
//     delete_id = del_id;
//     confirmDialog({
//       message: "Do you want to delete this record ?",
//       header: "Delete Confirmation",
//       icon: "pi pi-info-circle",
//       acceptClassName: "p=-button-danger",
//       accept,
//       reject,
//     });
//   };

//   const getDocMngRecords = async (id: any) => {
//     const collectData = {
//       docMid: id,
//       fileTypId: -1,
//       divisionid: -1,
//       subsubjId: -1,
//       user_Id: -1,
//       fileNo: "",
//       subDeptID: -1,
//       synopsis: "",
//       complt: ""
//     }
//     console.log("collectData", collectData);

//     const response = await axios.post(
//       HOST_URL.HOST_URL2 + `DocMangr/GetDocMangr`,
//       collectData
//     );

//     console.log("getData", response.data.data);
//     setDocDetailData(response.data.data[0]["fileNo"] + " - " + response.data.data[0]["fileType_Name"])
//     setDocDetailRecords(response.data.data);
//     setDrawerOpen(true);
//   }

//   const fetchZonesData = async () => {
//     try {
//       const collectData = {
//         docMid: -1,
//         fileTypId: -1,
//         //@ts-ignore
//         divisionid: devId === "null" ? -1 : devId,
//         subsubjId: -1,
//         user_Id: -1,
//         fileNo: "",
//         subDeptID: subDeptID == null ? -1 : subDeptID,
//         synopsis: "",
//         complt: ""
//       };
//       console.log("collectData", collectData);
//       const response = await axios.post(
//         HOST_URL.HOST_URL2 + `DocMangr/GetDocMangr`,
//         collectData
//       );
//       console.log("result", response.data.data);
//       const data = response.data.data;
//       const zonesWithIds = data.map((doc: any, index: any) => ({
//         ...doc,
//         serialNo: index + 1,
//         id: doc.docMid,
//       }));

//       setZones(zonesWithIds);
//       setIsLoading(false);

//       if (data.length > 0) {
//         const columns: GridColDef[] = [
//           {
//             field: "actions",
//             headerClassName: "MuiDataGrid-colCell",
//             headerName: t("text.Action"),
//             width: 150,

//             renderCell: (params) => {
//               return [
//                 <Stack
//                   spacing={1}
//                   direction="row"
//                   sx={{ alignItems: "center", marginTop: "5px" }}
//                 >
//                   <EditIcon
//                     style={{
//                       fontSize: "20px",
//                       color: "blue",
//                       cursor: "pointer",
//                     }}
//                     className="cursor-pointer"
//                     onClick={() => routeChangeEdit(params.row)}
//                   />

//                   <DeleteIcon
//                     style={{
//                       fontSize: "20px",
//                       color: "red",
//                       cursor: "pointer",
//                     }}
//                     onClick={() => {
//                       handledeleteClick(params.row.id);
//                     }}
//                   />
//                   <VisibilityIcon
//                     style={{
//                       fontSize: "20px",
//                       color: "blue",
//                       cursor: "pointer",
//                     }}
//                     onClick={() => {
//                       getDocMngRecords(params.row.id);
//                     }}
//                   />
//                 </Stack>,
//               ];
//             },
//           },
//           {
//             field: "serialNo",
//             headerName: t("text.SrNo"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 60
//           },
//           {
//             field: "fileNo",
//             headerName: t("text.FileNo"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 140
//           },

//           {
//             field: "synopsis",
//             headerName: t("text.FileName"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 140
//           },
//           {
//             field: "fileDef",
//             headerName: t("text.FileDef"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 140
//           },
//         ];
//         setColumns(columns as any);
//       }
//     } catch (error) {
//       console.error("Error fetching data:", error);
//     }
//   };

//   // 🔵 When File Selected → Only Open Modal
//   const handleExcelFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const file = event.target.files?.[0];
//     if (!file) return;

//     if (!file.name.match(/\.(xlsx|xls)$/)) {
//       toast.error("Please select a valid Excel file (.xlsx or .xls)");
//       return;
//     }

//     setSelectedExcelFile(file);
//     setExcelModalOpen(true); // Open modal
//   };

//   // Excel Import Function
//   // const handleExcelImport = async (event: React.ChangeEvent<HTMLInputElement>) => {

//   //   const data = secureGetItem("userdata");

//   //   const file = event.target.files?.[0];
//   //   if (!file) return;

//   //   // Check if file is Excel format
//   //   if (!file.name.match(/\.(xlsx|xls)$/)) {
//   //     toast.error("Please select a valid Excel file (.xlsx or .xls)");
//   //     return;
//   //   }

//   //   setImportLoading(true);

//   //   try {
//   //     const formData = new FormData();
//   //     formData.append('file', file);

//   //     // Add required form fields based on your API
//   //     formData.append('ContentType', '');
//   //     formData.append('ContentDisposition', '');
//   //     formData.append('Headers', JSON.stringify({}));
//   //     formData.append('Length', '');
//   //     formData.append('Name', '');
//   //     formData.append('FileName', file.name);
//   //     formData.append('userId', data[0].useR_ID); // You might want to add actual user ID here

//   //     const response = await axios.post(
//   //       `${HOST_URL.HOST_URL}Import/ImportExcel`,
//   //       formData
//   //     );

//   //     // console.log("response->>>",response.data)

//   //     if (response.data.success) {
//   //       toast.success("Excel file imported successfully!");
//   //       // Refresh the data grid after successful import
//   //       fetchZonesData();
//   //     } else {
//   //       toast.error(response.data.mesg || "Failed to import Excel file");
//   //     }
//   //   } catch (error: any) {
//   //     console.error("Error importing Excel file:", error);
//   //     toast.error(error.response?.data?.message || "Error importing Excel file");
//   //   } finally {
//   //     setImportLoading(false);
//   //     // Reset the file input
//   //     event.target.value = '';
//   //   }
//   // };


//   // 🔵 Final API call when user clicks IMPORT in modal
//   const handleFinalExcelImport = async () => {
//     if (!selectedExcelFile) {
//       toast.error("No file selected");
//       return;
//     }

//     setImportLoading(true);

//     try {
//       const data = secureGetItem("userdata");
//       const formData = new FormData();

//       formData.append("file", selectedExcelFile);
//       formData.append("FileName", selectedExcelFile.name);
//       formData.append("userId", data[0].useR_ID);

//       const response = await axios.post(
//         `${HOST_URL.HOST_URL}Import/ImportExcel`,
//         formData
//       );

//       if (response.data.success) {
//         toast.success("Excel imported successfully!");
//         fetchZonesData();
//       } else {
//         toast.error(response.data.mesg || "Failed to import Excel");
//       }
//     } catch (error) {
//       toast.error("Error importing Excel");
//     } finally {
//       setImportLoading(false);
//       setExcelModalOpen(false);
//       setSelectedExcelFile(null);
//     }
//   };

//   // Download Template Function
//   const downloadTemplate = () => {
//     try {
//       let ExcelTemplate = `UEsDBBQABgAIAAAAIQCq91ikegEAABQGAAATAAgCW0NvbnRlbnRfVHlwZXNdLnhtbCCiBAIooAACAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADMVMlqwzAQvRf6D0bXEitJoZQSJ4cuxzaQ9AMUa2yL2JLQTNLk7zt2FkrIgmmgvdjY0rxlhnmD0aoqoyUENM4mohd3RQQ2ddrYPBGf07fOo4iQlNWqdBYSsQYUo`;
//       let downloadUrl = ExcelTemplate;

//       // If it's a pure base64 string without data URL prefix, add the prefix
//       if (!ExcelTemplate.startsWith('data:') && !ExcelTemplate.startsWith('blob:')) {
//         downloadUrl = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${ExcelTemplate}`;
//       }

//       const link = document.createElement('a');
//       link.href = downloadUrl;
//       link.download = 'CSAFormat.xlsx';
//       link.style.display = 'none';
//       link.target = '_blank'; // Open in new tab as fallback

//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);

//       // Fallback for mobile devices
//       setTimeout(() => {
//         const opened = window.open(downloadUrl, '_blank');
//         if (!opened) {
//           toast.info("Please check your downloads folder for CSAFormat.xlsx");
//         }
//       }, 100);

//       toast.success("Template download started!");
//     } catch (error) {
//       console.error("Error downloading template:", error);
//       toast.error("Failed to download template");
//     }
//   };

//   const adjustedColumns = columns.map((column: any) => ({
//     ...column,
//   }));

//   return (
//     <>
//       <Card
//         style={{
//           width: "100%",
//           marginTop: "3vh",
//           backgroundColor: "#E9FDEE",
//         }}
//       >
//         <Paper
//           sx={{
//             width: "100%",
//             overflow: "hidden",
//             "& .MuiDataGrid-colCell": {
//               backgroundColor: `var(--grid-headerBackground)`,
//               color: `var(--grid-headerColor)`,
//               fontSize: 17,
//               fontWeight: 900
//             },
//           }}
//           style={{ padding: "10px" }}
//         >
//           <ConfirmDialog />

//           <Typography
//             gutterBottom
//             variant="h5"
//             component="div"
//             sx={{ padding: "20px" }}
//             align="left"
//           >
//             {t("text.DocumentEntry")}
//           </Typography>
//           <Divider />

//           <Box height={10} />

//           <Stack direction="row" spacing={2} classes="my-2 mb-2">
//             {/* Add Button */}
//             <Button
//               onClick={routeChangeAdd}
//               variant="contained"
//               endIcon={<AddCircleIcon />}
//               size="large"
//               style={{ backgroundColor: `var(--header-background)` }}
//             >
//               {t("text.add")}
//             </Button>

//             {/* Excel Import Button */}
//             <Button
//               component="label"
//               variant="contained"
//               endIcon={<PublishIcon />}
//               size="large"
//               style={{ backgroundColor: `var(--header-background)` }}
//               disabled={importLoading}
//             >
//               {importLoading ? "Importing..." : "Import Excel"}
//               <input
//                 type="file"
//                 hidden
//                 accept=".xlsx,.xls"
//                 onChange={handleExcelFileSelect}
//               />
//             </Button>

//             {/* Download Template Button */}
//             {/* <Button
//               onClick={downloadTemplate}
//               variant="contained"
//               endIcon={<DownloadIcon />}
//               size="large"
//               style={{ backgroundColor: `var(--header-background)` }}
//             >
//               Download Template
//             </Button> */}
//           </Stack>

//           <FileDetailSwipeableDrawer
//             open={drawerOpen}
//             onClose={() => setDrawerOpen(false)}
//             title={`# ${DocDetailData}  `}
//             userData={DocDetailRecords}
//           />

//           {isLoading ? (
//             <div
//               style={{
//                 display: "flex",
//                 justifyContent: "center",
//                 alignItems: "center",
//               }}
//             >
//               <CircularProgress />
//             </div>
//           ) : (
//             <Box>
//               <br />
//               <div style={{ width: "100%", backgroundColor: `var(--grid-background)` }}>
//                 <DataGrid
//                   rows={zones}
//                   columns={adjustedColumns}
//                   autoHeight
//                   slots={{
//                     toolbar: GridToolbar,
//                   }}
//                   rowSpacingType="border"
//                   pagination={true}
//                   pageSizeOptions={[5, 10, 25, 50, 100].map((size) => ({
//                     value: size,
//                     label: `${size}`,
//                   }))}
//                   initialState={{
//                     pagination: { paginationModel: { pageSize: 5 } },
//                   }}
//                   slotProps={{
//                     toolbar: {
//                       showQuickFilter: true,
//                     },
//                   }}
//                 />
//               </div>
//             </Box>
//           )}
//         </Paper>
//       </Card>
//       {/* 🔵 Excel Import Confirmation Modal */}
//       <Dialog open={excelModalOpen} onClose={() => setExcelModalOpen(false)}>
//         <DialogTitle>Confirm Excel Import</DialogTitle>

//         <DialogContent>
//           <Typography>
//             Selected File: <strong>{selectedExcelFile?.name}</strong>
//           </Typography>
//           <Typography mt={1}>
//             Click <strong>Import</strong> to upload this Excel file.
//           </Typography>
//         </DialogContent>

//         <DialogActions>
//           <Button color="error" onClick={() => setExcelModalOpen(false)}>
//             Cancel
//           </Button>

//           <Button
//             variant="contained"
//             onClick={handleFinalExcelImport}
//             disabled={importLoading}
//             style={{ backgroundColor: `var(--header-background)` }}
//           >
//             {importLoading ? "Importing..." : "Import"}
//           </Button>
//         </DialogActions>
//       </Dialog>

//       <ToastApp />
//     </>
//   );
// }
